<template>
	<App title="Admin Profile">
		<div class="profile-layout">

			<!-- Profile hero -->
			<div class="profile-hero">
				<div class="profile-avatar">
					{{ initials }}
				</div>
				<div class="profile-hero-info">
					<h2 class="profile-name">{{ admin.fullname }}</h2>
					<span class="profile-role-badge">{{ roleLabel }}</span>
					<p class="profile-email">{{ admin.email }}</p>
				</div>
				<div class="profile-hero-actions">
					<a :href="route('admin.page.group')" class="btn btn-sm btn-outline-secondary">
						<i class="fas fa-arrow-left me-1"></i> Back
					</a>
				</div>
			</div>

			<!-- Profile Information -->
			<div class="profile-card">
				<div class="profile-card-header">
					<h3 class="profile-card-title">Profile Information</h3>
					<a :href="route('admin.page.update', { id: admin.slug })" class="btn btn-sm btn-primary">
						<i class="fas fa-pencil-alt me-1"></i> Edit
					</a>
				</div>
				<div class="profile-card-body">
					<div class="profile-field">
						<p class="profile-field-label">Full Name</p>
						<p class="profile-field-value">{{ admin.fullname || '—' }}</p>
					</div>
					<div class="profile-field">
						<p class="profile-field-label">Email Address</p>
						<p class="profile-field-value">{{ admin.email || '—' }}</p>
					</div>
					<div class="profile-field">
						<p class="profile-field-label">Phone</p>
						<p class="profile-field-value">{{ admin.phone || '—' }}</p>
					</div>
					<div class="profile-field">
						<p class="profile-field-label">Access Level</p>
						<p class="profile-field-value">{{ roleLabel }}</p>
					</div>
					<div class="profile-field">
						<p class="profile-field-label">Workplace</p>
						<p class="profile-field-value">{{ workplace.title || '—' }}</p>
					</div>
				</div>
			</div>

		</div>
	</App>
</template>

<script lang="ts">
import type Admin from '@/models/Admin'
import App from '@/vue/templates/App.vue'
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'

@Component({
	components: { App },
})
class Single extends Vue {
	@Prop({ type: Object })
	readonly admin!: Admin

	get initials() {
		return (this.admin?.fullname ?? '')
			.split(' ')
			.slice(0, 2)
			.map((w) => w[0]?.toUpperCase() ?? '')
			.join('')
	}

	get roleLabel() {
		return (this.admin?.role ?? '')
			.replace(/_/g, ' ')
			.replace(/\b\w/g, (c) => c.toUpperCase())
	}

	get workplace() {
		return this.admin?.workplace ?? ''
	}
}

export default toNative(Single)
</script>

<style scoped lang="scss">
.profile-layout {
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

// ─── Hero card ────────────────────────────────────────────────────────────────
.profile-hero {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	background: #fff;
	border-radius: 12px;
	padding: 1.5rem 1.75rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	flex-wrap: wrap;
}

.profile-avatar {
	flex-shrink: 0;
	width: 72px;
	height: 72px;
	border-radius: 50%;
	background: linear-gradient(135deg, #1e3a2f, #3a7d52);
	color: #fff;
	font-size: 1.5rem;
	font-weight: 700;
	display: flex;
	align-items: center;
	justify-content: center;
	letter-spacing: 0.02em;
	box-shadow: 0 4px 12px rgba(30, 58, 47, 0.25);
}

.profile-hero-info {
	flex: 1;
	min-width: 0;
}

.profile-name {
	font-size: 1.25rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.3rem;
	line-height: 1.2;
}

.profile-role-badge {
	display: inline-block;
	padding: 0.2rem 0.7rem;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	background: #dcfce7;
	color: #15803d;
	margin-bottom: 0.4rem;
}

.profile-email {
	font-size: 0.85rem;
	color: #6b7280;
	margin: 0;
}

.profile-hero-actions {
	flex-shrink: 0;
	margin-left: auto;
	display: flex;
	gap: 0.5rem;
}

// ─── Info card ────────────────────────────────────────────────────────────────
.profile-card {
	background: #fff;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	overflow: hidden;
}

.profile-card-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 1.1rem 1.5rem;
	border-bottom: 1px solid #f1f3f5;
}

.profile-card-title {
	font-size: 1rem;
	font-weight: 700;
	color: #111827;
	margin: 0;
}

.profile-card-body {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 0;

	@media (max-width: 768px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (max-width: 480px) {
		grid-template-columns: 1fr;
	}
}

.profile-field {
	padding: 1.25rem 1.5rem;
	border-right: 1px solid #f1f3f5;
	border-bottom: 1px solid #f1f3f5;

	&:nth-child(3n) {
		border-right: none;
	}
}

.profile-field-label {
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #9ca3af;
	margin-bottom: 0.35rem;
}

.profile-field-value {
	font-size: 0.95rem;
	font-weight: 500;
	color: #111827;
	margin: 0;
	word-break: break-word;
}
</style>
